"use client";

import React from "react";
import { ArrowRight, Phone, TrendingUp, ShieldCheck, Activity } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function Hero() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Hotel Revenue Management in Ahmedabad",
    "serviceType": "Hospitality Revenue Management & OTA Consulting",
    "provider": {
      "@type": "ProfessionalService",
      "name": "HotelRevBaBa",
      "url": "https://hotelrevbaba.com",
      "telephone": "+91-79721-95929"
    },
    "areaServed": {
      "@type": "City",
      "name": "Ahmedabad",
      "containedInPlace": {
        "@type": "Country",
        "name": "India"
      }
    },
    "description": "Specialized hotel revenue management, dynamic pricing strategies, and OTA channel optimization for business hotels, heritage boutique properties, and stays across Ahmedabad.",
    "offers": {
      "@type": "Offer",
      "url": "https://hotelrevbaba.com/HotelRevenueManagementInAhmedabad",
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock"
    }
  };
  const proofStats = [{
    metric: "+12% to +28%",
    label: "Average RevPAR growth",
    description: "Measured across independent and boutique properties in Ahmedabad within 90 days.",
    icon: TrendingUp
  }, {
    metric: "<2%",
    label: "Parity violations",
    description: "Strict automated OTA monitoring to protect your direct booking advantage.",
    icon: ShieldCheck
  }, {
    metric: "24/7",
    label: "Rate intelligence",
    description: "Algorithmic market tracking calibrated for local seasonal demand peaks.",
    icon: Activity
  }];
  return <section id="hero" data-nav="dark" className="relative overflow-hidden bg-white text-foreground py-16 md:py-24 lg:py-32 isolate">
      {/* JSON-LD Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(schemaData)
    }} />

      {/* Decorative ambient gradients */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute top-1/2 -right-32 h-[30rem] w-[30rem] rounded-full bg-secondary/15 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          {/* Eyebrow badge */}
          <div className="inline-flex items-center gap-2 mb-6">
            <Badge variant="secondary" className="bg-secondary/20 text-secondary border border-secondary/30 px-3.5 py-1 text-xs sm:text-sm font-semibold tracking-wide uppercase">
              Ahmedabad Hospitality Consulting
            </Badge>
          </div>

          {/* Main H1 */}
          <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.15]">
            Hotel revenue management & OTA distribution consulting in Ahmedabad
          </h1>

          {/* Subheadline */}
          <p className="mt-6 text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
            Stop losing up to 20% of your margins to OTA commissions and unmonitored pricing leaks. We help independent, boutique, and chain properties maximize RevPAR, protect rate parity, and accelerate direct bookings.
          </p>

          {/* Call to action cluster */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <Button asChild size="lg" className="w-full sm:w-auto bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold px-8 py-6 text-base shadow-lg transition-transform hover:-translate-y-0.5 active:translate-y-0">
              <Link to="#" className="inline-flex items-center justify-center gap-2">
                <span>Schedule your free revenue audit</span>
                <ArrowRight className="size-5" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-primary/30 bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-6 text-base shadow-sm">
              <a href="tel:+917972195929" className="inline-flex items-center justify-center gap-2">
                <Phone className="size-5 text-secondary" />
                <span>+91 79721 95929</span>
              </a>
            </Button>
          </div>

          <p className="mt-4 text-xs sm:text-sm text-muted-foreground">
            Confidential evaluation · No long-term retainer required
          </p>
        </div>

        {/* Proof Bar / Key Performance Indicators & Visual */}
        <div className="mt-16 md:mt-24 max-w-5xl mx-auto">
          {/* Hotel hospitality visual banner */}
          <div className="mb-8 overflow-hidden rounded-lg border border-border shadow-xl bg-card">
            <div className="relative h-48 sm:h-64 md:h-72 w-full">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ahmedabad_hotel_revenue_management.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ahmedabad_hotel_revenue_management.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ahmedabad_hotel_revenue_management.webp-wvc-sizes" alt="Luxury business hotel suite in Ahmedabad illustrating performance analytics and hotel revenue management" loading="lazy" decoding="async" className="h-full w-full object-cover object-center brightness-90 contrast-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 flex flex-wrap items-center justify-between gap-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/80 backdrop-blur-md border border-border/30 text-xs sm:text-sm text-primary-foreground">
                  <span className="h-2 w-2 rounded-lg bg-secondary animate-pulse" />
                  <span className="font-medium">Market-Calibrated Yield Optimization</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {proofStats.map((stat, i) => {
            const IconComponent = stat.icon;
            return <div key={stat.label} data-index={i}>
                  <Card className="h-full border border-border bg-card text-foreground shadow-xl transition-[border-color,background-color] duration-300 hover:border-secondary/50 hover:bg-card/15">
                    <CardContent className="p-6 md:p-8 flex flex-col justify-between h-full">
                      <div>
                        <div className="flex items-center justify-between gap-4 mb-4">
                          <span className="font-default text-3xl lg:text-4xl font-extrabold tracking-tight text-secondary">
                            {stat.metric}
                          </span>
                          <div className="p-2.5 rounded-lg bg-secondary/15 text-secondary border border-secondary/20 shrink-0">
                            <IconComponent className="size-6" />
                          </div>
                        </div>
                        <h2 className="font-default text-lg font-semibold text-foreground">
                          {stat.label}
                        </h2>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground leading-normal">
                        {stat.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}